import { useState, useCallback, useEffect, useMemo } from 'react';
import { api } from '@services';
import {
  FinancialDataAPI,
  ADRMetricsData,
  OccupancyMetricsData,
  RevPARMetricsData,
  FinancialGridRow
} from '@features/ocr/types/types';

// Add rate limiting configuration
const RATE_LIMIT_DELAY = 1000; // 1 second delay between requests
let lastRequestTime = 0;

// Helper function to handle rate limiting
const rateLimitedRequest = async <T>(requestFn: () => Promise<T>): Promise<T> => {
  const now = Date.now();
  const timeSinceLastRequest = now - lastRequestTime;

  if (timeSinceLastRequest < RATE_LIMIT_DELAY) {
    await new Promise(resolve => setTimeout(resolve, RATE_LIMIT_DELAY - timeSinceLastRequest));
  }

  lastRequestTime = Date.now();
  return requestFn();
};

interface UseFinancialDataReturn {
  financialData: FinancialDataAPI | null;
  adrMetricsData: ADRMetricsData | null;
  occupancyMetricsData: OccupancyMetricsData | null;
  revparMetricsData: RevPARMetricsData | null;
  loading: boolean;
  error: string | null;
  success: string | null;
  availableDocTypes: string[];
  selectedDocType: string;
  availableDocTypeOptions: string[];
  fetchFinancialData: (hotelId: string) => Promise<void>;
  fetchADRMetricsData: (hotelId: string) => Promise<void>;
  fetchOccupancyMetricsData: (hotelId: string) => Promise<void>;
  fetchRevPARMetricsData: (hotelId: string) => Promise<void>;
  setSelectedDocType: (docType: string) => void;
  setSuccess: (message: string | null) => void;
  setError: (message: string | null) => void;
  setAvailableDocTypeOptions: (options: string[]) => void;
  gridRows: FinancialGridRow[];
  setGridRows: React.Dispatch<React.SetStateAction<FinancialGridRow[]>>;
}

export function useFinancialData(): UseFinancialDataReturn {
  const [financialData, setFinancialData] = useState<FinancialDataAPI | null>(null);
  const [adrMetricsData, setAdrMetricsData] = useState<ADRMetricsData | null>(null);
  const [occupancyMetricsData, setOccupancyMetricsData] = useState<OccupancyMetricsData | null>(null);
  const [revparMetricsData, setRevparMetricsData] = useState<RevPARMetricsData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [availableDocTypes, setAvailableDocTypes] = useState<string[]>([]);
  const [selectedDocType, setSelectedDocType] = useState<string>('');
  const [availableDocTypeOptions, setAvailableDocTypeOptions] = useState<string[]>([]);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  // Fetch financial data from API
  const fetchFinancialData = useCallback(async (hotelId: string) => {
    if (isFetching) {
      console.log('Already fetching financial data, skipping duplicate request');
      return;
    }

    setIsFetching(true);
    setLoading(true);
    setError(null);
    setSuccess(null);
    setFinancialData(null); // Clear previous data

    try {
      const response = await rateLimitedRequest(() =>
        api.get(`/ocr/hotel/${hotelId}/financials`)
      );
      console.log('Raw API Response:', response.data);

      // Basic validation of response structure
      if (response.data && response.data.categories && response.data.years && response.data.data) {
        // Check if Profit & Loss Statement exists in the data
        const hasPL = Object.keys(response.data.data).includes('Profit & Loss Statement');
        console.log('API Response - Document types:', Object.keys(response.data.data));
        console.log('API Response - Has Profit & Loss Statement:', hasPL);

        // Log the structure of the data for debugging
        Object.entries(response.data.data).forEach(([docType, docData]) => {
          console.log(`Document type ${docType} data:`, docData);
          Object.entries(docData as Record<string, Array<{ item_name: string; values: unknown; file_id?: string }>>).forEach(([catId, items]) => {
            items.forEach(item => {
              console.log(`Item ${item.item_name} in category ${catId}:`, {
                values: item.values,
                type: typeof item.values
              });
            });
          });
        });

        if (!hasPL) {
          console.log('Available document types:', Object.keys(response.data.data));
        }

        setFinancialData(response.data);
      } else {
        throw new Error('Received invalid financial data structure from API.');
      }
    } catch (err: unknown) {
      console.error('Error fetching financial data:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load financial data.';
      setError(errorMessage);
      setFinancialData(null);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  }, [isFetching]);

  // Fetch ADR metrics data from API
  const fetchADRMetricsData = useCallback(async (hotelId: string) => {
    if (isFetching) {
      console.log('Already fetching ADR metrics, skipping duplicate request');
      return;
    }

    setIsFetching(true);
    setLoading(true);
    setError(null);

    try {
      const response = await rateLimitedRequest(() =>
        api.get(`/ocr/hotel/${hotelId}/adr-metrics`)
      );
      setAdrMetricsData(response.data);
    } catch (err: unknown) {
      console.error('Error fetching ADR metrics:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load ADR metrics.';
      setError(errorMessage);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  }, [isFetching]);

  // Fetch Occupancy metrics data from API
  const fetchOccupancyMetricsData = useCallback(async (hotelId: string) => {
    if (isFetching) {
      console.log('Already fetching Occupancy metrics, skipping duplicate request');
      return;
    }

    setIsFetching(true);
    setLoading(true);
    setError(null);

    try {
      const response = await rateLimitedRequest(() =>
        api.get(`/ocr/hotel/${hotelId}/occupancy-metrics`)
      );
      setOccupancyMetricsData(response.data);
    } catch (err: unknown) {
      console.error('Error fetching Occupancy metrics:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load Occupancy metrics.';
      setError(errorMessage);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  }, [isFetching]);

  // Fetch RevPAR metrics data from API
  const fetchRevPARMetricsData = useCallback(async (hotelId: string) => {
    if (isFetching) {
      console.log('Already fetching RevPAR metrics, skipping duplicate request');
      return;
    }

    console.log('Fetching RevPAR metrics for hotel:', hotelId);
    setIsFetching(true);
    setLoading(true);
    setError(null);

    try {
      const response = await rateLimitedRequest(() =>
        api.get(`/ocr/hotel/${hotelId}/revpar-metrics`)
      );
      console.log('RevPAR metrics API response:', response.data);

      if (!response.data || !response.data.years) {
        console.warn('RevPAR API returned invalid data structure:', response.data);
        setRevparMetricsData({ years: [], months: {} });
        return;
      }

      setRevparMetricsData(response.data);
    } catch (err: unknown) {
      console.error('Error fetching RevPAR metrics:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load RevPAR metrics.';
      setError(errorMessage);
      // Make sure to set data to empty object in case of error
      setRevparMetricsData({ years: [], months: {} });
    } finally {
      console.log('Setting loading to false for RevPAR metrics');
      setLoading(false);
      setIsFetching(false);
    }
  }, [isFetching]);

  // Update available document types and default selection when financial data or metrics data changes
  useEffect(() => {
    let docTypes = financialData?.data ? Object.keys(financialData.data) : [];

    // Always add P&L
    if (!docTypes.includes('Profit & Loss Statement')) {
      docTypes.push('Profit & Loss Statement');
    }

    // Add STR Report if present in financialData
    if (financialData?.data && financialData.data['STR Report'] && !docTypes.includes('STR Report')) {
      docTypes.push('STR Report');
    }

    // Add ADR Metrics if there is data
    if (adrMetricsData && adrMetricsData.years && adrMetricsData.years.length > 0) {
      if (!docTypes.includes('ADR Metrics')) {
        docTypes.push('ADR Metrics');
      }
    }

    // Add Occupancy Metrics if there is data
    if (occupancyMetricsData && occupancyMetricsData.years && occupancyMetricsData.years.length > 0) {
      if (!docTypes.includes('Occupancy Metrics')) {
        docTypes.push('Occupancy Metrics');
      }
    }

    // Add RevPAR Metrics if there is data
    if (revparMetricsData && revparMetricsData.years && revparMetricsData.years.length > 0) {
      if (!docTypes.includes('RevPAR Metrics')) {
        docTypes.push('RevPAR Metrics');
      }
    }

    // Sort document types
    const sortedDocTypes = docTypes.sort((a, b) => {
      if (a === 'Profit & Loss Statement') return -1;
      if (b === 'Profit & Loss Statement') return 1;
      return a.localeCompare(b);
    });

    setAvailableDocTypes(prev => {
      if (JSON.stringify(prev) !== JSON.stringify(sortedDocTypes)) {
        return sortedDocTypes;
      }
      return prev;
    });

    setSelectedDocType(prev => {
      if (!prev || !docTypes.includes(prev)) {
        return sortedDocTypes[0] || '';
      }
      return prev;
    });
  }, [financialData, adrMetricsData, occupancyMetricsData, revparMetricsData]); // Depend on all metrics data

  const transformedRows = useMemo(() => {
    if (!financialData || !selectedDocType) return [];

    const transformFinancialDataToGridRows = (data: FinancialDataAPI): FinancialGridRow[] => {
      if (!data?.data) {
        console.warn('No data in financialData');
        return [];
      }

      console.log('Document types in API data:', Object.keys(data.data));
      console.log('Selected document type:', selectedDocType);

      // Handle the case where the selected document type doesn't exist in the data
      if (!data.data[selectedDocType]) {
        console.warn(`Selected document type "${selectedDocType}" not found in data`);

        // Try to find a matching document type with more flexible matching
        const documentTypes = Object.keys(data.data);
        const matchingType = documentTypes.find(type =>
          type.toLowerCase().includes(selectedDocType.toLowerCase()) ||
          selectedDocType.toLowerCase().includes(type.toLowerCase())
        );

        if (matchingType) {
          console.log(`Found matching document type: "${matchingType}" for "${selectedDocType}"`);
          // Use the matching type
          const docData = data.data[matchingType];
          return processDocumentData(docData, matchingType);
        }

        return [];
      }

      const docData = data.data[selectedDocType];
      return processDocumentData(docData, selectedDocType);
    };

    // Helper function to process document data
    const processDocumentData = (docData: Record<string, Array<{ item_name: string; values: unknown; file_id?: string }>>, docType: string): FinancialGridRow[] => {
      const rows: FinancialGridRow[] = [];

      for (const [categoryId, items] of Object.entries(docData)) {
        for (const item of items) {
          // Parse the values if they're stored as a string
          let values: { [year: string]: number } = {};
          try {
            if (typeof item.values === 'string') {
              console.log(`Parsing string values for ${item.item_name}:`, item.values);
              values = JSON.parse(item.values);
            } else if (typeof item.values === 'object' && item.values !== null) {
              const valueObj = item.values as Record<string, unknown>;
              // Convert the object to the expected format
              Object.entries(valueObj).forEach(([key, val]) => {
                if (val !== null && val !== undefined) {
                  const numValue = typeof val === 'number' ? val : parseFloat(String(val));
                  if (!isNaN(numValue)) {
                    values[key] = numValue;
                  }
                }
              });
              console.log(`Processed object values for ${item.item_name}:`, values);
            }
          } catch (error) {
            console.error('Error parsing values for item:', item.item_name, error);
          }

          // Create the base row
          const row: FinancialGridRow = {
            id: `${categoryId}-${item.item_name}`,
            internalId: parseInt(categoryId),
            originalCategoryId: parseInt(categoryId),
            originalItemName: item.item_name,
            categoryId: parseInt(categoryId),
            itemName: item.item_name,
            fileId: item.file_id,
            documentType: docType,
          };

          // Add each year's value to the row - use direct assignment to preserve number types
          Object.entries(values).forEach(([year, value]) => {
            if (value !== null && value !== undefined) {
              // Ensure it's stored as a number
              row[year] = value;
              console.log(`Setting year ${year} for ${item.item_name} to:`, value, typeof value);
            }
          });

          console.log('Created row:', row);
          // Verify the row has the expected values with their types
          Object.entries(row).forEach(([key, value]) => {
            if (!isNaN(Number(key))) {
              console.log(`Row ${row.id} - year ${key}:`, value, typeof value);
            }
          });

          rows.push(row);
        }
      }

      return rows;
    };

    const rows = transformFinancialDataToGridRows(financialData);
    console.log('Transformed rows:', rows);
    return rows;
  }, [financialData, selectedDocType]);

  const [gridRows, setGridRows] = useState<FinancialGridRow[]>([]);

  useEffect(() => {
    console.log('Setting grid rows:', transformedRows);
    setGridRows(transformedRows);
  }, [transformedRows]);

  console.log('Raw financial data:', financialData);
  console.log('Transformed rows:', transformedRows);
  console.log('Grid rows before filtering:', gridRows);

  return {
    financialData,
    adrMetricsData,
    occupancyMetricsData,
    revparMetricsData,
    loading,
    error,
    success,
    availableDocTypes,
    selectedDocType,
    availableDocTypeOptions,
    fetchFinancialData,
    fetchADRMetricsData,
    fetchOccupancyMetricsData,
    fetchRevPARMetricsData,
    setSelectedDocType,
    setSuccess,
    setError,
    setAvailableDocTypeOptions,
    gridRows,
    setGridRows
  };
}